import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../api";
import { isResponseSuccess } from "../utils";
import { URLS } from "../constants";
import { IClientPlaces } from "../all-types";

/**
 * Fetch all places from the backend.
 */
export const getPlaceList = createAsyncThunk(
  "places/getPlaceList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_PLACES);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch places");
      }
      return response.data.data as IClientPlaces[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch places");
    }
  }
);

/**
 * Create a new place entry.
 */
export const createPlace = createAsyncThunk(
  "places/createPlace",
  async (details: { place: Omit<IClientPlaces, '_id'> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post(URLS.ADD_PLACE, { place: details.place });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to add place");
      }
      dispatch(getPlaceList()); // Refresh list after adding
      return response.data.data as IClientPlaces;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not add new place");
    }
  }
);

/**
 * Update an existing place.
 */
export const updatePlace = createAsyncThunk(
  "places/updatePlace",
  async (details: { id: string; place: Omit<IClientPlaces, '_id'> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put(URLS.UPDATE_PLACE(details.id), details.place);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update place");
      }
      dispatch(getPlaceList());
      return response.data.data as IClientPlaces;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update place");
    }
  }
);

/**
 * Delete a place.
 */
export const deletePlace = createAsyncThunk(
  "places/deletePlace",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(URLS.DELETE_PLACE(id));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to delete place");
      }
      dispatch(getPlaceList());
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not delete place");
    }
  }
);
